import "../styles/ui/deferred-video-play.scss";

import React, {useCallback, useEffect, useRef, useState} from "react";
import {getBannerVideoSources} from "../helpers/bannerVideoSources";

// Keep the source URL out of the media element until playback is requested.
const DeferredVideo = ({src, type, desktopAv1, mobile, ...videoProps}) => {
    const videoRef = useRef(null);
    const loadedSources = useRef("");
    const av1SourceUrl = useRef("");
    const av1Failed = useRef(false);
    const visible = useRef(false);
    const playbackRequested = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [manualPlayback, setManualPlayback] = useState(false);

    const loadSources = useCallback(() => {
        const video = videoRef.current;
        const sources = getBannerVideoSources(
            {src, type, desktopAv1: av1Failed.current ? null : desktopAv1, mobile},
            window.matchMedia("(min-width: 768px)").matches,
            value => video.canPlayType(value)
        );
        const signature = JSON.stringify(sources);
        if (loadedSources.current !== signature) {
            av1SourceUrl.current = "";
            video.querySelectorAll("source").forEach(source => source.remove());
            sources.forEach(({src: url, type: mime}) => {
                const source = document.createElement("source");
                if (mime) source.type = mime;
                source.src = url;
                if (sources.length > 1 && url === desktopAv1?.mediaItemUrl) av1SourceUrl.current = source.src;
                video.appendChild(source);
            });
            loadedSources.current = signature;
            video.load();
            return true;
        }
        return false;
    }, [src, type, desktopAv1, mobile]);

    const play = useCallback(() => {
        const video = videoRef.current;
        playbackRequested.current = true;
        loadSources();
        const promise = video.play();
        if (promise) promise.catch(() => {}); // The play button remains available if autoplay is blocked.
    }, [loadSources]);

    useEffect(() => {
        const video = videoRef.current;
        const desktop = window.matchMedia("(min-width: 768px)");
        const pause = () => {
            playbackRequested.current = false;
            video.pause();
        };
        const updatePlayback = () => {
            if (visible.current && desktop.matches) play();
            else pause();
        };
        const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
            visible.current = entry.isIntersecting;
            if (!visible.current) pause();
            else if (desktop.matches) play();
        });
        observer?.observe(video.parentElement);
        if (desktop.addEventListener) desktop.addEventListener("change", updatePlayback);
        else desktop.addListener(updatePlayback);

        return () => {
            observer?.disconnect();
            if (desktop.removeEventListener) desktop.removeEventListener("change", updatePlayback);
            else desktop.removeListener(updatePlayback);
            pause();
        };
    }, [play]);

    return (
        <>
            <video
                {...videoProps}
                ref={videoRef}
                preload="none"
                loop
                muted
                playsInline
                controls={manualPlayback}
                onPlay={() => {
                    const video = videoRef.current;
                    if (video.paused) return;
                    playbackRequested.current = true;
                    // Native controls can resume after a viewport change too.
                    // Re-select before continuing that user-requested playback.
                    if (loadSources()) play();
                    setPlaying(true);
                }}
                onPause={() => {
                    const video = videoRef.current;
                    if (video.paused && !video.error) playbackRequested.current = false;
                    setPlaying(false);
                }}
                onError={() => {
                    // Some platforms advertise a codec but cannot decode it.
                    // Retry the main source once if AV1 failed during playback.
                    if (!av1Failed.current && av1SourceUrl.current && videoRef.current.currentSrc === av1SourceUrl.current) {
                        av1Failed.current = true;
                        if (visible.current && playbackRequested.current && window.matchMedia("(min-width: 768px)").matches) play();
                    }
                }}
            />
            {!playing && (
                <button
                    className="deferred-video-play"
                    type="button"
                    aria-label="Play video"
                    onClick={() => {
                        setManualPlayback(true);
                        play();
                    }}
                >
                    <span aria-hidden="true">▶</span> Play
                </button>
            )}
        </>
    );
};

export default DeferredVideo;
